// apps/client/src/hooks/useMessengerDmMode.ts
//
// Single source of truth for the per-tenant Messenger DM mode flag.
//
// Reads the SUBCOLLECTION doc `companies/{c}/featureFlags/meta_messenger_dm`
// (field `mode`) — the SAME doc the backend reads in
// `metaMessengerWebhook.resolveMessengerFlag` and
// `sendOutboundMessage`. Previously the composer gate read the company-doc
// `featureFlags` map (`company.featureFlags.meta_messenger_dm.mode`), which is
// a DIFFERENT Firestore location that is never written for this flag — so the
// UI resolved 'off' (Send disabled) even when the backend was at
// 'observe'/'enforce'. This hook removes that mismatch.
//
// Firestore rules: the company catch-all (`match /{subcollection}/{docId}`)
// grants read to any signed-in company member for `featureFlags` (not on the
// exclusion list), so every role that can reach the DM inbox can read it.
//
// Fail-closed: defaults to 'off' on missing doc, unexpected value, or read
// error (and logs the error — no silent-fail on rule-gated reads).
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

export type MessengerDmMode = 'off' | 'observe' | 'enforce';

export function useMessengerDmMode(
  companyId: string | undefined,
): MessengerDmMode {
  const [mode, setMode] = useState<MessengerDmMode>('off');

  useEffect(() => {
    if (!companyId) {
      setMode('off');
      return;
    }
    const ref = doc(db, `companies/${companyId}/featureFlags/meta_messenger_dm`);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const raw = snap.exists()
          ? (snap.data()?.mode as string | undefined)
          : undefined;
        setMode(raw === 'observe' || raw === 'enforce' ? raw : 'off');
      },
      (err) => {
        // Fail closed; surface for diagnosis rather than swallowing.
        console.error(
          '[useMessengerDmMode] flag read failed; defaulting to off',
          err,
        );
        setMode('off');
      },
    );
    return () => unsub();
  }, [companyId]);

  return mode;
}
